export type Rates = Record<string, number>;
export type TimeSeries = Record<string, Record<string, number>>;

const CACHE_KEY = 'exchange_latest';
const CACHE_TTL_MS = 1000 * 60 * 60; // 1 hour

// Fallback exchange rates (updated February 2026 - approximate)
const FALLBACK_RATES: Record<string, Rates> = {
  USD: {
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    JPY: 149.5,
    INR: 83.12,
    MGA: 4520.0, // Malagasy Ariary
  },
  EUR: {
    USD: 1.09,
    EUR: 1,
    GBP: 0.86,
    JPY: 162.5,
    INR: 90.35,
    MGA: 4913.04,
  },
  GBP: {
    USD: 1.27,
    EUR: 1.16,
    GBP: 1,
    JPY: 189.24,
    INR: 105.58,
    MGA: 5736.63,
  },
  JPY: {
    USD: 0.0067,
    EUR: 0.0062,
    GBP: 0.0053,
    JPY: 1,
    INR: 0.56,
    MGA: 30.23,
  },
  INR: {
    USD: 0.012,
    EUR: 0.011,
    GBP: 0.0095,
    JPY: 1.79,
    INR: 1,
    MGA: 54.38,
  },
  MGA: {
    USD: 0.00022,
    EUR: 0.0002,
    GBP: 0.00017,
    JPY: 0.033,
    INR: 0.018,
    MGA: 1,
  },
};

async function fetchJson(url: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * Fetch latest rates (cached for TTL). Falls back to hardcoded rates if API fails.
 */
export async function getLatestRates(
  base = 'USD',
  symbols?: string[]
): Promise<Rates> {
  // Try to get from cache first
  try {
    const cache = localStorage.getItem(CACHE_KEY);
    if (cache) {
      const parsed = JSON.parse(cache);
      if (Date.now() - parsed.ts < CACHE_TTL_MS && parsed.base === base) {
        console.log('✅ Using cached exchange rates');
        return parsed.rates;
      }
    }
  } catch {
    /* ignore parse errors */
  }

  // Try to fetch from API
  try {
    console.log(`🌐 Fetching exchange rates for ${base} from API...`);
    const symbolsQuery = symbols ? `&symbols=${symbols.join(',')}` : '';

    // Try exchangerate-api.com (free, no key needed)
    const data = await fetchJson(
      `https://api.exchangerate-api.com/v4/latest/${base}`
    );

    const rates: Rates = data?.rates || {};

    if (Object.keys(rates).length > 0) {
      console.log(
        `✅ Successfully fetched ${Object.keys(rates).length} exchange rates`
      );
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ ts: Date.now(), base, rates })
      );
      return rates;
    }

    throw new Error('No rates returned from API');
  } catch (apiError) {
    console.warn(
      '⚠️ Exchange rate API failed, using fallback rates:',
      apiError
    );

    // Use fallback rates
    const fallbackRates = FALLBACK_RATES[base] || FALLBACK_RATES.USD;

    // Cache the fallback rates for shorter time (15 minutes)
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ ts: Date.now(), base, rates: fallbackRates })
      );
    } catch {
      /* ignore storage errors */
    }

    console.log(`✅ Using fallback rates for ${base}`);
    return fallbackRates;
  }
}

/**
 * Fetch timeseries for a symbol vs base between start and end (YYYY-MM-DD).
 * Returns an object mapping date => { SYMBOL: number }
 */
export async function getTimeSeries(
  base = 'USD',
  symbol = 'EUR',
  start: string,
  end: string
): Promise<TimeSeries> {
  try {
    // Try exchangerate-api.com historical endpoint
    const data = await fetchJson(
      `https://api.exchangerate-api.com/v4/latest/${base}`
    );

    // This API doesn't support time series, so we'll return current rate for all dates
    // This is a limitation - for production, you'd want a paid API with historical data
    const currentRate = data?.rates?.[symbol] || 1;

    // Generate dummy time series with current rate
    const result: TimeSeries = {};
    const startDate = new Date(start);
    const endDate = new Date(end);

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const dateStr = d.toISOString().split('T')[0];
      result[dateStr] = { [symbol]: currentRate };
    }

    return result;
  } catch (error) {
    console.warn('⚠️ Time series API failed, using fallback:', error);

    // Use fallback rate
    const fallbackRates = FALLBACK_RATES[base] || FALLBACK_RATES.USD;
    const fallbackRate = fallbackRates[symbol] || 1;

    const result: TimeSeries = {};
    const startDate = new Date(start);
    const endDate = new Date(end);

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const dateStr = d.toISOString().split('T')[0];
      result[dateStr] = { [symbol]: fallbackRate };
    }

    return result;
  }
}

/**
 * Test if exchange rate API is working
 */
export async function testExchangeRateAPI(): Promise<{
  working: boolean;
  source: 'api' | 'fallback';
  rates?: Rates;
  error?: string;
}> {
  try {
    const data = await fetchJson(
      'https://api.exchangerate-api.com/v4/latest/USD'
    );
    const rates: Rates = data?.rates || {};

    if (Object.keys(rates).length > 0) {
      return { working: true, source: 'api', rates };
    }

    return {
      working: false,
      source: 'fallback',
      rates: FALLBACK_RATES.USD,
      error: 'No rates returned',
    };
  } catch (error) {
    return {
      working: false,
      source: 'fallback',
      rates: FALLBACK_RATES.USD,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
